import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, AlertCircle, Shield, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

type LoginStep = 'credentials' | 'mfa-verify' | 'mfa-setup';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [attemptsHint, setAttemptsHint] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>('credentials');
  const [mfaToken, setMfaToken] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const { login, confirmMfaSetup, verifyMfaLogin } = useAuth();
  const navigate = useNavigate();

  const qrImageUrl = otpauthUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUrl)}`
    : '';

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setAttemptsHint('');
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.status === 'authenticated') {
        navigate('/');
        return;
      }
      if (result.status === 'mfa_required') {
        setMfaToken(result.mfaToken);
        setStep('mfa-verify');
        setOtpCode('');
        return;
      }
      if (result.status === 'mfa_setup_required') {
        setSetupToken(result.setupToken);
        setOtpauthUrl(result.otpauthUrl);
        setStep('mfa-setup');
        setOtpCode('');
        return;
      }
      if (result.status === 'error') {
        setError(result.error || 'Email ou mot de passe incorrect');
      }
    } catch {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setAttemptsHint('');
    setIsLoading(true);
    try {
      const result =
        step === 'mfa-setup'
          ? await confirmMfaSetup(setupToken, otpCode)
          : await verifyMfaLogin(mfaToken, otpCode);
      if (result.status === 'authenticated') {
        navigate('/');
        return;
      }
      if (result.status === 'error') {
        setError(result.error || 'Code invalide');
        if (result.attemptsRemaining != null) {
          setAttemptsHint(`${result.attemptsRemaining} essai(s) restant(s)`);
        }
        if (result.locked) {
          setAttemptsHint('Compte temporairement verrouillé — réessayez dans 5 minutes');
        }
      }
    } catch {
      setError('Erreur de vérification MFA.');
    } finally {
      setIsLoading(false);
    }
  };

  const backToCredentials = () => {
    setStep('credentials');
    setOtpCode('');
    setMfaToken('');
    setSetupToken('');
    setOtpauthUrl('');
    setError('');
    setAttemptsHint('');
  };

  const title =
    step === 'credentials' ? 'Connexion' : step === 'mfa-setup' ? 'Configuration MFA' : 'Vérification MFA';

  const subtitle =
    step === 'credentials'
      ? 'Accédez à votre espace PRIsME - Outil interne BZ Inc.'
      : step === 'mfa-setup'
        ? 'Scannez le QR avec Microsoft Authenticator, puis entrez le code à 6 chiffres.'
        : 'Code à 6 chiffres requis à chaque connexion. Valide 5 min, 5 essais max.';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-4"
    >
      <motion.div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </motion.div>

      <motion.div className="relative w-full max-w-md">
        <motion.div className="text-center mb-8">
          <motion.img
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            src="/PrismeLogo.png"
            alt="PRIsME Logo"
            className="h-24 w-auto mx-auto drop-shadow-2xl mb-4"
          />
          <p className="text-purple-200 text-lg font-semibold">Plateforme interne BZ Inc.</p>
          <p className="text-purple-300 text-sm mt-2">L'intelligence au service de l'excellence TI</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-purple-200"
        >
          <motion.div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              {step !== 'credentials' && <Shield className="w-6 h-6 text-purple-600" />}
              {title}
            </h2>
            <p className="text-gray-600">{subtitle}</p>
          </motion.div>

          {step === 'credentials' ? (
            <form onSubmit={handleCredentials} className="space-y-5">
              <motion.div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-100 focus:border-purple-500 bg-white text-gray-900"
                    placeholder="votre@email.com"
                    autoComplete="email"
                  />
                </div>
              </motion.div>

              <motion.div>
                <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-2">
                  Mot de passe
                </label>
                <motion.div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-100 focus:border-purple-500 bg-white text-gray-900"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </motion.div>
              </motion.div>

              {error && <ErrorBox message={error} />}
              <SubmitButton isLoading={isLoading} label="Se connecter" />
            </form>
          ) : (
            <form onSubmit={handleMfaSubmit} className="space-y-5">
              {step === 'mfa-setup' && qrImageUrl && (
                <motion.div className="flex flex-col items-center gap-3 p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <img src={qrImageUrl} alt="QR code MFA" className="w-[200px] h-[200px] rounded-lg" />
                </motion.div>
              )}

              <motion.div>
                <label htmlFor="otp" className="block text-sm font-bold text-gray-700 mb-2">
                  Code à 6 chiffres
                </label>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-center text-2xl tracking-[0.4em] font-mono focus:ring-4 focus:ring-purple-100 focus:border-purple-500"
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
              </motion.div>

              {error && <ErrorBox message={error} />}
              {attemptsHint && (
                <p className="text-sm text-amber-700 font-medium text-center">{attemptsHint}</p>
              )}

              <SubmitButton
                isLoading={isLoading}
                label={step === 'mfa-setup' ? 'Activer MFA et se connecter' : 'Vérifier et se connecter'}
              />

              <button
                type="button"
                onClick={backToCredentials}
                className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-purple-700 py-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </button>
            </form>
          )}

          <motion.div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500">
              {step === 'credentials'
                ? 'Authentification sécurisée JWT'
                : 'MFA TOTP · Session 5 min · 5 essais maximum'}
            </p>
          </motion.div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-6 text-purple-200 text-sm"
        >
          © 2025 BZ Inc. • Tous droits réservés
        </motion.p>
      </motion.div>
    </motion.div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-2 p-4 bg-red-50 border-2 border-red-200 rounded-xl"
    >
      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
      <p className="text-sm font-semibold text-red-800">{message}</p>
    </motion.div>
  );
}

function SubmitButton({ isLoading, label }: { isLoading: boolean; label: string }) {
  return (
    <motion.button
      type="submit"
      disabled={isLoading}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white py-3.5 px-6 rounded-xl font-bold text-lg shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
    >
      {isLoading ? (
        <>
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          En cours...
        </>
      ) : (
        <>
          <Lock className="w-5 h-5" />
          {label}
        </>
      )}
    </motion.button>
  );
}
